/**
 * Utilidades de validación para formularios
 *
 * Funciones para validar campos comunes en la aplicación
 * como emails, RUTs, contraseñas y campos requeridos.
 */

/**
 * Valida si un email tiene formato válido
 *
 * @param email - Email a validar
 * @returns true si el email es válido
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Valida si un RUT tiene formato válido (chileno)
 *
 * @param rut - RUT a validar
 * @returns true si el RUT tiene formato válido
 */
export const validateRut = (rut: string): boolean => {
  // Formato: XX.XXX.XXX-X o X-X
  const rutRegex = /^(\d{1,2}\.\d{3}\.\d{3}-[\dkK]|\d{1,2}-\d{1,2})$/;
  return rutRegex.test(rut.trim());
};

/**
 * Valida si un campo requerido no está vacío
 *
 * @param value - Valor a validar
 * @returns true si el valor no está vacío
 */
export const validateRequired = (value: string): boolean => {
  return value.trim().length > 0;
};

/**
 * Valida si un nombre tiene formato válido
 *
 * @param name - Nombre a validar
 * @returns true si el nombre es válido
 */
export const validateName = (name: string): boolean => {
  // Solo letras, espacios y algunos caracteres especiales
  const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;
  return nameRegex.test(name.trim()) && name.trim().length >= 2;
};

/**
 * Valida una contraseña y retorna errores específicos
 *
 * @param password - Contraseña a validar
 * @returns Array de errores (vacío si la contraseña es válida)
 */
export const validatePassword = (password: string): string[] => {
  const errors: string[] = [];

  if (password.length < 6) {
    errors.push("La contraseña debe tener al menos 6 caracteres");
  }

  if (!/\d/.test(password)) {
    errors.push("La contraseña debe contener al menos 1 número");
  }

  if (!/[a-zA-Z]/.test(password)) {
    errors.push("La contraseña debe contener al menos 1 letra");
  }

  if (/\s/.test(password)) {
    errors.push("La contraseña no puede contener espacios");
  }

  return errors;
};

/**
 * Valida si al menos un rol está seleccionado
 *
 * @param roles - Array de roles seleccionados
 * @returns true si al menos un rol está seleccionado
 */
export const validateRoles = (roles: string[]): boolean => {
  return roles.length > 0;
};

/**
 * Obtiene el mensaje de error para un campo específico
 *
 * @param fieldName - Nombre del campo
 * @param value - Valor del campo
 * @param isTouched - Si el campo ha sido tocado
 * @returns Mensaje de error o null si no hay error
 */
export const getFieldError = (
  fieldName: string,
  value: string,
  isTouched: boolean
): string | null => {
  if (!isTouched) return null;

  if (!validateRequired(value)) {
    return `${fieldName} es requerido`;
  }

  switch (fieldName.toLowerCase()) {
    case "email":
      if (!validateEmail(value)) {
        return "Formato de email inválido";
      }
      break;
    case "rut":
      if (!validateRut(value)) {
        return "Formato de RUT inválido (ej: 12.345.678-9 o 1-9)";
      }
      break;
    case "nombres":
    case "apellidos":
      if (!validateName(value)) {
        return `${fieldName} debe contener solo letras y tener al menos 2 caracteres`;
      }
      break;
    case "contraseña":
      const passwordErrors = validatePassword(value);
      if (passwordErrors.length > 0) {
        return passwordErrors[0];
      }
      break;
  }

  return null;
};

/**
 * Valida un formulario completo de usuario
 *
 * @param formData - Datos del formulario
 * @returns Objeto con errores por campo
 */
export const validateUserForm = (formData: {
  nombres: string;
  apellidos: string;
  rut: string;
  email: string;
  password: string;
  roles: string[];
}): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Validar campos requeridos
  if (!validateRequired(formData.nombres)) {
    errors.nombres = "Nombres es requerido";
  } else if (!validateName(formData.nombres)) {
    errors.nombres =
      "Nombres debe contener solo letras y tener al menos 2 caracteres";
  }

  if (!validateRequired(formData.apellidos)) {
    errors.apellidos = "Apellidos es requerido";
  } else if (!validateName(formData.apellidos)) {
    errors.apellidos =
      "Apellidos debe contener solo letras y tener al menos 2 caracteres";
  }

  if (!validateRequired(formData.rut)) {
    errors.rut = "RUT es requerido";
  } else if (!validateRut(formData.rut)) {
    errors.rut = "Formato de RUT inválido (ej: 12.345.678-9 o 1-9)";
  }

  if (!validateRequired(formData.email)) {
    errors.email = "Email es requerido";
  } else if (!validateEmail(formData.email)) {
    errors.email = "Formato de email inválido";
  }

  if (!validateRequired(formData.password)) {
    errors.password = "Contraseña es requerida";
  } else {
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      errors.password = passwordErrors[0];
    }
  }

  if (!validateRoles(formData.roles)) {
    errors.roles = "Debe seleccionar al menos un rol";
  }

  return errors;
};
